'use strict';
import datePickerTemplate from 'components/inputs/date-picker/date-picker.hbs'
import DomUtils from 'helpers/dom-utils'

export class DatePickerInput {

  constructor(originalElement) {
    this.originalElement = originalElement;
  }

  static padTo2Digits(n) {
    let parsed = Math.abs(Math.trunc(parseInt(n)));
    let number = parsed.toString();
    if (isNaN(parsed)) number = "1";
    if (number.length < 2) number = "0" + number;
    return number;
  }

  static isValidDate(year, month, day) {
    const maybeDate = new Date(year, month - 1, day);
    const yearOk = maybeDate.getFullYear() === year;
    const monthOk = maybeDate.getMonth() + 1 === month;
    const dayOk = maybeDate.getDate() === day;
    return yearOk && monthOk && dayOk;
  }

  static defaultMonthList(limitedDateRange) {
    if (limitedDateRange) {
      const today = new Date();
      return [today.getMonth() === 0 ? 12 : today.getMonth(), today.getMonth() + 1];
    } else {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    }
  }

  static defaultYearList(limitedDateRange) {
    if (limitedDateRange) {
      const today = new Date();
      if (today.getMonth() < 2) {
        return [today.getFullYear() - 1, today.getFullYear()]
      } else {
        return [today.getFullYear()];
      }
    } else {
      return [2017, 2018, 2019, 2020, 2021];
    }
  }

  static defaultDayList(limitedDateRange) {
    `${limitedDateRange}`;
    let d = [];
    for (let i = 1; i <= 31; i++) {
      d.push(i);
    }
    return d;
  }

  static findDatePickerDivElement(aFieldInComponent) {
    if (!aFieldInComponent || !aFieldInComponent.parentElement) return null;
    else if (aFieldInComponent.parentElement.matches('.date-picker--container')) return aFieldInComponent.parentElement;
    else return DatePickerInput.findDatePickerDivElement(aFieldInComponent.parentElement)
  }

  createChangeListener() {
    const elem = this.originalElement;
    return () => {
      const day = document.getElementById(elem.id + '--day');
      const month = document.getElementById(elem.id + '--month');
      const year = document.getElementById(elem.id + '--year');

      const dayValue = parseInt(day.value);
      const monthValue = parseInt(month.value);
      const yearValue = parseInt(year.value);

      if (DatePickerInput.isValidDate(yearValue, monthValue, dayValue)) {
        elem.value = DatePickerInput.padTo2Digits(dayValue) + '/'
          + DatePickerInput.padTo2Digits(monthValue)
          + '/' + DatePickerInput.padTo2Digits(yearValue);
        elem.classList.remove('invalid');
      } else {
        elem.classList.add('invalid');
      }
    }
  }

  static addChangeListenerToInputs(newDom, changeListener) {
    const newFields = newDom.querySelectorAll('select');
    for (let i = 0; i < newFields.length; i++) {
      newFields.item(i).addEventListener('change', changeListener);
    }
  }

  static makeInputsRequiredIfOriginalRequired(newDom, isRequired) {
    const newFields = newDom.querySelectorAll('select');
    for (let i = 0; i < newFields.length; i++) {
      newFields.item(i).required = isRequired;
    }
  }

  static addAriaDescribedByLabelToDayIfOnOriginalField(dayElement, describedByValue) {
    if (dayElement && describedByValue && !(describedByValue.length === 0)) {
      dayElement.setAttribute('aria-describedby', describedByValue);
    }
  }

  expandDateComponent(template) {
    const changeListener = this.createChangeListener(this.originalElement);
    const limitedDateRange = this.originalElement.hasAttribute('data-limited-range');

    const lists = this.initialSelectListData(limitedDateRange);

    const newComponentHTML = template(
      {
        "original-element-id": this.originalElement.id,
        "day-options": lists[0],
        "month-options": lists[1],
        "year-options": lists[2]
      }
    );
    const newComponent = DomUtils.createElementsFromString(newComponentHTML)[0];
    this.setValuesFromOriginalField(newComponent);
    DatePickerInput.makeInputsRequiredIfOriginalRequired(newComponent, this.originalElement.required);
    DatePickerInput.addAriaDescribedByLabelToDayIfOnOriginalField(
      newComponent.querySelector(`#${this.originalElement.id}--day`),
      this.originalElement.getAttribute('aria-describedby')
    );

    DatePickerInput.addChangeListenerToInputs(newComponent, changeListener);
    this.originalElement.parentNode.insertBefore(newComponent, this.originalElement);
    this.originalElement.type = "hidden";

    this.originalElement.setAttribute('data-component-expanded', '');
  }

  augmentTitleForScreenReaders() {
    const originalLabel = document.querySelector(`label[for="${this.originalElement.id}"`);
    const dayField = document.getElementById(this.originalElement.id + '--day');
    const monthField = document.getElementById(this.originalElement.id + '--month');
    const yearField = document.getElementById(this.originalElement.id + '--year');

    dayField.setAttribute('aria-labelled-by', originalLabel.id);
    monthField.setAttribute('aria-labelled-by', originalLabel.id);
    yearField.setAttribute('aria-labelled-by', originalLabel.id)
  }

  setValuesFromOriginalField(newDom) {
    if ((/\d+\/\d+\/\d+/.test(this.originalElement.value))) {
      const dayField = newDom.querySelector(`#${this.originalElement.id}--day`);
      const monthField = newDom.querySelector(`#${this.originalElement.id}--month`);
      const yearField = newDom.querySelector(`#${this.originalElement.id}--year`);

      const splitValues = this.originalElement.value.split('/');

      dayField.value = splitValues[0];
      const selectedDay = dayField.querySelector(`option[value="${parseInt(splitValues[0])}"]`);
      if (selectedDay) selectedDay.selected = true;

      monthField.value = splitValues[1];
      const selectedMonth = monthField.querySelector(`option[value="${parseInt(splitValues[1])}"]`);
      if (selectedMonth) selectedMonth.selected = true;

      yearField.value = splitValues[2];
      const selectedYear = yearField.querySelector(`option[value="${parseInt(splitValues[2])}"]`);
      if (selectedYear) selectedYear.selected = true;
    }
  }

  initialSelectListData(limitedDateRange) {
    return [
      DatePickerInput.defaultDayList(limitedDateRange).map(d => {
        return {text: DatePickerInput.padTo2Digits(d), value: d}
      }),
      DatePickerInput.defaultMonthList(limitedDateRange).map(m => {
        return {text: DatePickerInput.padTo2Digits(m), value: m}
      }),
      DatePickerInput.defaultYearList(limitedDateRange).map(y => {
        return {text: y, value: y}
      })]
  }
}

if (document.querySelectorAll) {
  let dateInputs = document.querySelectorAll('input[type="date"][data-date-picker]:not([data-component-expanded])');

  for (let i = 0; i < dateInputs.length; i++) {
    const elem = dateInputs.item(i);
    new DatePickerInput(elem).expandDateComponent(datePickerTemplate);
  }
}
